import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";

// Pages
import PortfolioPage from "@/pages/PortfolioPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import AdminDashboard from "@/pages/AdminDashboard";
import SettingsPage from "@/pages/SettingsPage";
import NewArticlePage from "@/pages/NewArticlePage";
import TeamPage from "@/pages/TeamPage";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// Context for Auth
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Theme Context
export const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  // Apply theme on mount and change
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          localStorage.removeItem("token");
          setToken(null);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <AuthContext.Provider value={{ user, token, login, logout }}>
          <div className="App min-h-screen bg-background">
            <BrowserRouter>
              <Navbar />
              <main className="min-h-screen">
                <Routes>
                  <Route path="/" element={<BlogPage />} />
                  <Route path="/portfolio" element={<PortfolioPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/article/:slug" element={<BlogPostPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/a-propos" element={<AboutPage />} />
                  <Route path="/equipe" element={<TeamPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/login" />} />
                  <Route
                    path="/editor/new-article"
                    element={
                      user?.role === "admin" || user?.role === "editor" ? (
                        <NewArticlePage />
                      ) : (
                        <Navigate to="/login" />
                      )
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      user?.role === "admin" || user?.role === "editor" ? (
                        <AdminDashboard />
                      ) : (
                        <Navigate to="/login" />
                      )
                    }
                  />
                </Routes>
              </main>
              <Footer />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </div>
        </AuthContext.Provider>
      </ThemeContext.Provider>
    </LanguageProvider>
  );
}

export default App;
